/**
 * Craftable Meshes — Tier 1 & Tier 2
 * 3D meshes and onPlace callbacks for all craftable items.
 * Each mesh is kept low-poly (under 200 verts) using MeshStandardMaterial.
 */

import * as THREE from 'three';
import { registerOnPlace } from '../systems/crafting.js';

// ---------------------------------------------------------------------------
// Shared materials (reused across meshes to reduce draw calls)
// ---------------------------------------------------------------------------
const BROWN_WOOD = () => new THREE.MeshStandardMaterial({
  color: 0xa0826d,
  roughness: 0.95,
  metalness: 0.0,
});

const DARK_BROWN = () => new THREE.MeshStandardMaterial({
  color: 0x6b4226,
  roughness: 0.9,
  metalness: 0.0,
});

const STONE_GRAY = () => new THREE.MeshStandardMaterial({
  color: 0x7a7a7a,
  roughness: 0.85,
  metalness: 0.05,
});

const FLAME_ORANGE = () => new THREE.MeshStandardMaterial({
  color: 0xff6600,
  emissive: 0xff4400,
  emissiveIntensity: 0.8,
  roughness: 0.5,
  metalness: 0.0,
});

const FLAME_RED = () => new THREE.MeshStandardMaterial({
  color: 0xff2200,
  emissive: 0xff1100,
  emissiveIntensity: 0.6,
  roughness: 0.5,
  metalness: 0.0,
});

const FLAME_YELLOW = () => new THREE.MeshStandardMaterial({
  color: 0xffcc00,
  emissive: 0xffaa00,
  emissiveIntensity: 1.0,
  roughness: 0.4,
  metalness: 0.0,
});

const SHELL_WHITE = () => new THREE.MeshStandardMaterial({
  color: 0xfff0e0,
  roughness: 0.6,
  metalness: 0.1,
});

const STRING_BROWN = () => new THREE.MeshStandardMaterial({
  color: 0x8b6914,
  roughness: 0.9,
  metalness: 0.0,
});

// ---------------------------------------------------------------------------
// 1. Campfire
// ---------------------------------------------------------------------------
/**
 * Creates a campfire mesh group: ring of stones, inward-angled logs, flame cones.
 * Fits within ~1 unit radius.
 * @returns {THREE.Group}
 */
export function createCampfireMesh() {
  const group = new THREE.Group();

  // --- Stone ring (6 stones in a circle, radius 0.5) ---
  const stoneCount = 6;
  const ringRadius = 0.5;
  const stoneMat = STONE_GRAY();
  for (let i = 0; i < stoneCount; i++) {
    const stoneGeo = new THREE.SphereGeometry(0.12, 6, 4);
    stoneGeo.scale(1.0, 0.7, 1.0); // slightly flattened
    const stone = new THREE.Mesh(stoneGeo, stoneMat);
    const angle = (i / stoneCount) * Math.PI * 2;
    stone.position.set(
      Math.cos(angle) * ringRadius,
      0.06,
      Math.sin(angle) * ringRadius
    );
    stone.castShadow = true;
    stone.receiveShadow = false;
    group.add(stone);
  }

  // --- Driftwood logs (4 logs angled inward) ---
  const logCount = 4;
  const logMat = DARK_BROWN();
  for (let i = 0; i < logCount; i++) {
    const logGeo = new THREE.CylinderGeometry(0.04, 0.03, 0.6, 5);
    const log = new THREE.Mesh(logGeo, logMat);
    const angle = (i / logCount) * Math.PI * 2 + Math.PI / logCount;
    // Position base at the stone ring, angle inward toward center
    log.position.set(
      Math.cos(angle) * 0.25,
      0.15,
      Math.sin(angle) * 0.25
    );
    // Tilt inward: rotate around the tangent axis
    log.rotation.x = Math.sin(angle) * 0.5;
    log.rotation.z = -Math.cos(angle) * 0.5;
    log.castShadow = true;
    log.receiveShadow = false;
    group.add(log);
  }

  // --- Flame (3 stacked cones with slight rotation) ---
  const flameData = [
    { height: 0.35, radius: 0.15, y: 0.25, mat: FLAME_ORANGE(), rotY: 0 },
    { height: 0.25, radius: 0.10, y: 0.35, mat: FLAME_YELLOW(), rotY: 0.3 },
    { height: 0.18, radius: 0.07, y: 0.42, mat: FLAME_RED(), rotY: -0.2 },
  ];
  for (const fd of flameData) {
    const flameGeo = new THREE.ConeGeometry(fd.radius, fd.height, 6);
    const flame = new THREE.Mesh(flameGeo, fd.mat);
    flame.position.set(0, fd.y, 0);
    flame.rotation.y = fd.rotY;
    flame.castShadow = false;
    flame.receiveShadow = false;
    group.add(flame);
  }

  return group;
}

// ---------------------------------------------------------------------------
// 2. Wind Chime
// ---------------------------------------------------------------------------
/**
 * Creates a wind chime mesh group: horizontal driftwood bar with hanging shells.
 * ~0.8 units tall.
 * @returns {THREE.Group}
 */
export function createWindChimeMesh() {
  const group = new THREE.Group();

  // --- Horizontal driftwood bar ---
  const barGeo = new THREE.CylinderGeometry(0.03, 0.03, 0.5, 6);
  const barMat = BROWN_WOOD();
  const bar = new THREE.Mesh(barGeo, barMat);
  bar.rotation.z = Math.PI / 2; // lay horizontal
  bar.position.y = 0.75;
  bar.castShadow = true;
  bar.receiveShadow = false;
  group.add(bar);

  // --- Hanging loop at top (for attachment) ---
  const loopGeo = new THREE.TorusGeometry(0.04, 0.01, 4, 8);
  const loopMat = STRING_BROWN();
  const loop = new THREE.Mesh(loopGeo, loopMat);
  loop.position.y = 0.80;
  loop.castShadow = false;
  loop.receiveShadow = false;
  group.add(loop);

  // --- Shell pendants (4 shells hanging from strings) ---
  const shellMat = SHELL_WHITE();
  const stringMat = STRING_BROWN();
  const shellCount = 4;
  const barWidth = 0.4;

  // Store pendants for animation reference
  group.userData.pendants = [];

  for (let i = 0; i < shellCount; i++) {
    const pendant = new THREE.Group();

    // String (thin cylinder)
    const stringLength = 0.2 + Math.random() * 0.15; // varied lengths
    const stringGeo = new THREE.CylinderGeometry(0.005, 0.005, stringLength, 4);
    const string = new THREE.Mesh(stringGeo, stringMat);
    string.position.y = -stringLength / 2;
    string.castShadow = false;
    string.receiveShadow = false;
    pendant.add(string);

    // Shell (small cone shape, like a hanging shell)
    const shellGeo = new THREE.ConeGeometry(0.04, 0.08, 5);
    const shell = new THREE.Mesh(shellGeo, shellMat);
    shell.position.y = -stringLength - 0.04;
    shell.rotation.x = Math.PI; // point downward
    shell.castShadow = true;
    shell.receiveShadow = false;
    pendant.add(shell);

    // Position along the bar
    const xOffset = -barWidth / 2 + (i / (shellCount - 1)) * barWidth;
    pendant.position.set(xOffset, 0.75, 0);

    // Store initial phase for animation
    pendant.userData.swingPhase = Math.random() * Math.PI * 2;
    pendant.userData.swingSpeed = 1.5 + Math.random() * 0.5;
    pendant.userData.swingAmplitude = 0.08 + Math.random() * 0.04;

    group.userData.pendants.push(pendant);
    group.add(pendant);
  }

  return group;
}

// ---------------------------------------------------------------------------
// 3. Tiki Torch
// ---------------------------------------------------------------------------
/**
 * Creates a tiki torch mesh group: tall pole with bowl and flame at top.
 * ~2 units tall.
 * @returns {THREE.Group}
 */
export function createTikiTorchMesh() {
  const group = new THREE.Group();

  // --- Pole (tall cylinder) ---
  const poleGeo = new THREE.CylinderGeometry(0.06, 0.06, 2.0, 6);
  const poleMat = DARK_BROWN();
  const pole = new THREE.Mesh(poleGeo, poleMat);
  pole.position.y = 1.0; // center of 2-unit tall pole
  pole.castShadow = true;
  pole.receiveShadow = false;
  group.add(pole);

  // --- Bowl at top (inverted cone) ---
  const bowlGeo = new THREE.ConeGeometry(0.15, 0.15, 8);
  const bowlMat = DARK_BROWN();
  const bowl = new THREE.Mesh(bowlGeo, bowlMat);
  bowl.position.y = 1.95;
  bowl.rotation.x = Math.PI; // invert the cone
  bowl.castShadow = true;
  bowl.receiveShadow = false;
  group.add(bowl);

  // --- Flame (small cone, orange emissive) ---
  const flameGeo = new THREE.ConeGeometry(0.08, 0.2, 6);
  const flameMat = FLAME_ORANGE();
  const flame = new THREE.Mesh(flameGeo, flameMat);
  flame.position.y = 2.1;
  flame.castShadow = false;
  flame.receiveShadow = false;
  group.add(flame);

  return group;
}

// ---------------------------------------------------------------------------
// 4. Hammock
// ---------------------------------------------------------------------------
/**
 * Creates a hammock mesh group: two posts with fabric slung between them.
 * ~2 units wide, fits between two palm trees.
 * @returns {THREE.Group}
 */
export function createHammockMesh() {
  const group = new THREE.Group();

  // --- Two vertical posts ---
  const postMat = new THREE.MeshStandardMaterial({
    color: 0x6b4226,
    roughness: 0.9,
    metalness: 0.0,
  });

  const postGeo = new THREE.CylinderGeometry(0.05, 0.05, 1.5, 6);
  const postLeft = new THREE.Mesh(postGeo, postMat);
  postLeft.position.set(-1.0, 0.75, 0);
  postLeft.castShadow = true;
  postLeft.receiveShadow = false;
  group.add(postLeft);

  const postRight = new THREE.Mesh(postGeo, postMat);
  postRight.position.set(1.0, 0.75, 0);
  postRight.castShadow = true;
  postRight.receiveShadow = false;
  group.add(postRight);

  // --- Fabric slung between posts (curved box shape) ---
  const fabricMat = new THREE.MeshStandardMaterial({
    color: 0xCC6633,
    roughness: 0.8,
    metalness: 0.0,
    side: THREE.DoubleSide,
  });

  // Create a curved fabric using a series of box segments
  const segmentCount = 5;
  const totalWidth = 1.6; // slightly less than post spacing
  const segWidth = totalWidth / segmentCount;
  for (let i = 0; i < segmentCount; i++) {
    const t = (i / (segmentCount - 1)) - 0.5; // -0.5 to 0.5
    const sag = t * t * 1.2; // parabolic sag, deepest in center
    const segGeo = new THREE.BoxGeometry(segWidth + 0.02, 0.03, 0.6, 1, 1, 1);
    const seg = new THREE.Mesh(segGeo, fabricMat);
    const xPos = -totalWidth / 2 + segWidth * i + segWidth / 2;
    seg.position.set(xPos, 1.2 - sag, 0);
    // Tilt each segment to follow the curve
    if (i < segmentCount - 1) {
      const nextSag = ((i + 1) / (segmentCount - 1) - 0.5) ** 2 * 1.2;
      const angle = Math.atan2(sag - nextSag, segWidth);
      seg.rotation.z = angle * 0.5;
    }
    seg.castShadow = true;
    seg.receiveShadow = false;
    group.add(seg);
  }

  // --- Tie ropes at each end (thin cylinders from post top to fabric) ---
  const ropeMat = new THREE.MeshStandardMaterial({
    color: 0x8b6914,
    roughness: 0.9,
    metalness: 0.0,
  });
  const ropeGeo = new THREE.CylinderGeometry(0.015, 0.015, 0.35, 4);

  const ropeLeft = new THREE.Mesh(ropeGeo, ropeMat);
  ropeLeft.position.set(-0.85, 1.3, 0);
  ropeLeft.rotation.z = 0.3;
  ropeLeft.castShadow = false;
  ropeLeft.receiveShadow = false;
  group.add(ropeLeft);

  const ropeRight = new THREE.Mesh(ropeGeo, ropeMat);
  ropeRight.position.set(0.85, 1.3, 0);
  ropeRight.rotation.z = -0.3;
  ropeRight.castShadow = false;
  ropeRight.receiveShadow = false;
  group.add(ropeRight);

  return group;
}

// ---------------------------------------------------------------------------
// 5. Fishing Rod Stand
// ---------------------------------------------------------------------------
/**
 * Creates a fishing rod stand mesh group: vertical pole with cross-bar
 * and fishing line hanging toward water.
 * @returns {THREE.Group}
 */
export function createFishingRodStandMesh() {
  const group = new THREE.Group();

  const woodMat = new THREE.MeshStandardMaterial({
    color: 0x8B6914,
    roughness: 0.9,
    metalness: 0.0,
  });

  // --- Main vertical pole ---
  const poleGeo = new THREE.CylinderGeometry(0.04, 0.05, 1.8, 6);
  const pole = new THREE.Mesh(poleGeo, woodMat);
  pole.position.set(0, 0.9, 0);
  pole.castShadow = true;
  pole.receiveShadow = false;
  group.add(pole);

  // --- Cross-bar at top ---
  const crossGeo = new THREE.CylinderGeometry(0.03, 0.03, 0.8, 6);
  const crossBar = new THREE.Mesh(crossGeo, woodMat);
  crossBar.position.set(0.2, 1.75, 0);
  crossBar.rotation.z = Math.PI / 2; // lay horizontal
  crossBar.castShadow = true;
  crossBar.receiveShadow = false;
  group.add(crossBar);

  // --- Fishing line (thin cylinder hanging at an angle from cross-bar tip) ---
  const lineMat = new THREE.MeshStandardMaterial({
    color: 0xcccccc,
    roughness: 0.3,
    metalness: 0.2,
  });
  const lineGeo = new THREE.CylinderGeometry(0.008, 0.008, 1.2, 4);
  const line = new THREE.Mesh(lineGeo, lineMat);
  line.position.set(0.55, 1.2, 0);
  line.rotation.z = 0.4; // angle toward water
  line.castShadow = false;
  line.receiveShadow = false;
  group.add(line);

  // --- Small hook at line end ---
  const hookMat = new THREE.MeshStandardMaterial({
    color: 0xaaaaaa,
    roughness: 0.4,
    metalness: 0.6,
  });
  const hookGeo = new THREE.TorusGeometry(0.03, 0.008, 4, 6, Math.PI);
  const hook = new THREE.Mesh(hookGeo, hookMat);
  hook.position.set(0.78, 0.62, 0);
  hook.rotation.x = Math.PI / 2;
  hook.castShadow = false;
  hook.receiveShadow = false;
  group.add(hook);

  return group;
}

// ---------------------------------------------------------------------------
// 6. Sand Castle
// ---------------------------------------------------------------------------
/**
 * Creates a sand castle mesh group: main tower with cone top, smaller turrets,
 * and a flag on top.
 * @returns {THREE.Group}
 */
export function createSandCastleMesh() {
  const group = new THREE.Group();

  const sandMat = new THREE.MeshStandardMaterial({
    color: 0xF4D03F,
    roughness: 0.95,
    metalness: 0.0,
  });

  // --- Main tower ---
  const towerGeo = new THREE.CylinderGeometry(0.3, 0.35, 0.6, 8);
  const tower = new THREE.Mesh(towerGeo, sandMat);
  tower.position.set(0, 0.3, 0);
  tower.castShadow = true;
  tower.receiveShadow = false;
  group.add(tower);

  // --- Cone roof on main tower ---
  const roofGeo = new THREE.ConeGeometry(0.32, 0.25, 8);
  const roof = new THREE.Mesh(roofGeo, sandMat);
  roof.position.set(0, 0.72, 0);
  roof.castShadow = true;
  roof.receiveShadow = false;
  group.add(roof);

  // --- Smaller turrets around the base ---
  const turretData = [
    { x: 0.4, z: 0.25, height: 0.4, radius: 0.15 },
    { x: -0.4, z: 0.25, height: 0.35, radius: 0.13 },
    { x: 0.0, z: -0.4, height: 0.38, radius: 0.14 },
  ];
  for (const td of turretData) {
    const turretGeo = new THREE.CylinderGeometry(td.radius, td.radius + 0.02, td.height, 6);
    const turret = new THREE.Mesh(turretGeo, sandMat);
    turret.position.set(td.x, td.height / 2, td.z);
    turret.castShadow = true;
    turret.receiveShadow = false;
    group.add(turret);

    // Small cone on each turret
    const capGeo = new THREE.ConeGeometry(td.radius + 0.01, 0.12, 6);
    const cap = new THREE.Mesh(capGeo, sandMat);
    cap.position.set(td.x, td.height + 0.06, td.z);
    cap.castShadow = true;
    cap.receiveShadow = false;
    group.add(cap);
  }

  // --- Flag on top of main tower ---
  // Flag pole
  const poleMat = new THREE.MeshStandardMaterial({
    color: 0x8b6914,
    roughness: 0.9,
    metalness: 0.0,
  });
  const poleGeo = new THREE.CylinderGeometry(0.01, 0.01, 0.3, 4);
  const pole = new THREE.Mesh(poleGeo, poleMat);
  pole.position.set(0, 0.99, 0);
  pole.castShadow = false;
  pole.receiveShadow = false;
  group.add(pole);

  // Flag triangle (using a small cone flattened and rotated)
  const flagMat = new THREE.MeshStandardMaterial({
    color: 0xFF4444,
    roughness: 0.7,
    metalness: 0.0,
    side: THREE.DoubleSide,
  });
  const flagShape = new THREE.Shape();
  flagShape.moveTo(0, 0);
  flagShape.lineTo(0.12, 0.04);
  flagShape.lineTo(0, 0.08);
  flagShape.closePath();
  const flagGeo = new THREE.ShapeGeometry(flagShape);
  const flag = new THREE.Mesh(flagGeo, flagMat);
  flag.position.set(0.01, 1.06, 0);
  flag.castShadow = false;
  flag.receiveShadow = false;
  group.add(flag);

  return group;
}

// ---------------------------------------------------------------------------
// 7. Flower Garden
// ---------------------------------------------------------------------------
/**
 * Creates a flower garden mesh group: circular base patch with several
 * simple flowers (stem + bloom) in various colors.
 * @returns {THREE.Group}
 */
export function createFlowerGardenMesh() {
  const group = new THREE.Group();

  // --- Circular base patch (flat cylinder) ---
  const baseMat = new THREE.MeshStandardMaterial({
    color: 0x5C4033,
    roughness: 0.95,
    metalness: 0.0,
  });
  const baseGeo = new THREE.CylinderGeometry(0.6, 0.6, 0.05, 10);
  const base = new THREE.Mesh(baseGeo, baseMat);
  base.position.set(0, 0.025, 0);
  base.castShadow = false;
  base.receiveShadow = true;
  group.add(base);

  // --- Flowers ---
  const bloomColors = [0xFF69B4, 0xFF4500, 0xFFFF00, 0xFF69B4, 0xFFFF00];
  const stemMat = new THREE.MeshStandardMaterial({
    color: 0x228B22,
    roughness: 0.8,
    metalness: 0.0,
  });
  const leafMat = new THREE.MeshStandardMaterial({
    color: 0x2E8B57,
    roughness: 0.8,
    metalness: 0.0,
  });

  const flowerPositions = [
    { x: 0.0, z: 0.0, h: 0.45 },
    { x: 0.25, z: 0.2, h: 0.35 },
    { x: -0.3, z: 0.15, h: 0.4 },
    { x: 0.15, z: -0.25, h: 0.38 },
    { x: -0.2, z: -0.2, h: 0.32 },
  ];

  for (let i = 0; i < flowerPositions.length; i++) {
    const fp = flowerPositions[i];
    const flowerGroup = new THREE.Group();

    // Stem (thin green cylinder)
    const stemGeo = new THREE.CylinderGeometry(0.015, 0.015, fp.h, 4);
    const stem = new THREE.Mesh(stemGeo, stemMat);
    stem.position.set(0, fp.h / 2, 0);
    stem.castShadow = true;
    stem.receiveShadow = false;
    flowerGroup.add(stem);

    // Bloom (small sphere on top)
    const bloomMat = new THREE.MeshStandardMaterial({
      color: bloomColors[i],
      roughness: 0.6,
      metalness: 0.0,
    });
    const bloomGeo = new THREE.SphereGeometry(0.06, 6, 5);
    const bloom = new THREE.Mesh(bloomGeo, bloomMat);
    bloom.position.set(0, fp.h + 0.04, 0);
    bloom.castShadow = true;
    bloom.receiveShadow = false;
    flowerGroup.add(bloom);

    // Leaf (small flattened cone on the stem)
    const leafGeo = new THREE.ConeGeometry(0.04, 0.08, 4);
    leafGeo.scale(1.0, 1.0, 0.4); // flatten
    const leaf = new THREE.Mesh(leafGeo, leafMat);
    leaf.position.set(0.04, fp.h * 0.4, 0);
    leaf.rotation.z = -Math.PI / 4; // angle outward
    leaf.castShadow = false;
    leaf.receiveShadow = false;
    flowerGroup.add(leaf);

    flowerGroup.position.set(fp.x, 0.05, fp.z);
    group.add(flowerGroup);
  }

  return group;
}

// ---------------------------------------------------------------------------
// Master factory
// ---------------------------------------------------------------------------
/**
 * Create a craftable mesh by recipe ID.
 * @param {string} recipeId - recipe key from RECIPE_REGISTRY
 * @returns {THREE.Group|null}
 */
export function createCraftableMesh(recipeId) {
  const creators = {
    campfire: createCampfireMesh,
    wind_chime: createWindChimeMesh,
    tiki_torch: createTikiTorchMesh,
    hammock: createHammockMesh,
    fishing_rod_stand: createFishingRodStandMesh,
    sand_castle: createSandCastleMesh,
    flower_garden: createFlowerGardenMesh,
  };
  const fn = creators[recipeId];
  if (!fn) return null;
  const mesh = fn();
  mesh.userData.recipeId = recipeId;
  return mesh;
}

// ---------------------------------------------------------------------------
// onPlace callbacks
// ---------------------------------------------------------------------------

// Track active wind chimes for animation updates
const activeWindChimes = [];

/**
 * Returns the campfire onPlace callback.
 * Adds a warm PointLight and simple ember particles.
 * @param {THREE.Scene} scene
 * @returns {Function}
 */
export function getCampfireOnPlace(scene) {
  return (group) => {
    // Warm point light
    const light = new THREE.PointLight(0xFF6600, 0.8, 8);
    light.position.set(0, 0.5, 0);
    group.add(light);

    // Ember particles — 5 small orange spheres that drift upward
    const emberCount = 5;
    const emberMat = new THREE.MeshStandardMaterial({
      color: 0xff8800,
      emissive: 0xff4400,
      emissiveIntensity: 0.9,
      roughness: 0.5,
      metalness: 0.0,
    });

    const embers = [];
    for (let i = 0; i < emberCount; i++) {
      const emberGeo = new THREE.SphereGeometry(0.02, 4, 3);
      const ember = new THREE.Mesh(emberGeo, emberMat);
      ember.castShadow = false;
      ember.receiveShadow = false;

      // Random starting position above the fire
      ember.position.set(
        (Math.random() - 0.5) * 0.3,
        0.3 + Math.random() * 0.3,
        (Math.random() - 0.5) * 0.3
      );

      ember.userData.baseY = ember.position.y;
      ember.userData.phase = Math.random() * Math.PI * 2;
      ember.userData.speed = 0.3 + Math.random() * 0.4;
      ember.userData.drift = (Math.random() - 0.5) * 0.01;

      group.add(ember);
      embers.push(ember);
    }

    // Store embers for update
    group.userData.embers = embers;

    // Ember update function — called from the animate loop
    group.userData.updateEmbers = (time) => {
      for (const ember of embers) {
        const t = (time * ember.userData.speed + ember.userData.phase) % (Math.PI * 2);
        // Rise and reset in a cycle
        const rise = (Math.sin(t) + 1) * 0.5; // 0..1
        ember.position.y = ember.userData.baseY + rise * 0.6;
        ember.position.x += ember.userData.drift;
        // Fade out near top
        ember.material.opacity = 1.0 - rise * 0.7;
        ember.material.transparent = true;
        // Reset drift when it gets too far
        if (Math.abs(ember.position.x) > 0.4) {
          ember.position.x = (Math.random() - 0.5) * 0.2;
        }
      }
    };
  };
}

/**
 * Returns the wind chime onPlace callback.
 * Registers the chime for gentle swinging animation.
 * @param {THREE.Scene} _scene
 * @returns {Function}
 */
export function getWindChimeOnPlace(_scene) {
  return (group) => {
    activeWindChimes.push(group);
  };
}

/**
 * Returns the tiki torch onPlace callback.
 * Adds a warm PointLight near the top.
 * @param {THREE.Scene} scene
 * @returns {Function}
 */
export function getTikiTorchOnPlace(scene) {
  return (group) => {
    const light = new THREE.PointLight(0xFF8800, 0.6, 6);
    light.position.set(0, 2.0, 0);
    group.add(light);
  };
}

// ---------------------------------------------------------------------------
// Animation update for wind chimes (call from main animate loop)
// ---------------------------------------------------------------------------
/**
 * Update all active wind chime animations.
 * @param {number} time - elapsed time from clock
 */
export function updateWindChimes(time) {
  for (const chime of activeWindChimes) {
    const pendants = chime.userData.pendants;
    if (!pendants) continue;
    for (const pendant of pendants) {
      const { swingPhase, swingSpeed, swingAmplitude } = pendant.userData;
      // Gentle pendulum swing around Z axis
      pendant.rotation.z = Math.sin(time * swingSpeed + swingPhase) * swingAmplitude;
    }
  }
}

// ---------------------------------------------------------------------------
// Registration — wire up all craftable onPlace callbacks
// ---------------------------------------------------------------------------
/**
 * Register all craftable onPlace callbacks with the recipe registry.
 * Call once after scene initialization.
 *
 * Tier 2 items (hammock, fishing_rod_stand, sand_castle, flower_garden) have
 * no special onPlace effects yet — they will be added in later phases:
 *   - Hammock: beach bum behavior (Phase 2b)
 *   - Fishing Rod Stand: fish jumping (Phase 2b)
 *   - Sand Castle: erosion (pi-t0j.3)
 *   - Flower Garden: butterfly particles (pi-t0j.10)
 *
 * @param {THREE.Scene} scene
 */
export function registerCraftableCallbacks(scene) {
  // Tier 1 callbacks
  registerOnPlace('campfire', getCampfireOnPlace(scene));
  registerOnPlace('wind_chime', getWindChimeOnPlace(scene));
  registerOnPlace('tiki_torch', getTikiTorchOnPlace(scene));
  // Tier 2 — no onPlace callbacks yet (reserved for future phases)
}
